// Report inputs pre-attach — faithful ports of the four classic payload
// builders (NewProfile.addInputsForReport, NewMarketProfile.addInputsForReport,
// NewValuationReport.addInputsForReport, CreateReportFormat.companyData +
// NewCompanyReport.addCompanyReport). Every key mirrors the classic payloads
// exactly; deviations are bugs.
//
//   financial  → POST /user/report/save  JSON body {type:'if'|'spif', action:'save', data:{flags + 32 fields/yr}}
//   market     → POST /user/report/save  JSON body {type:'imr', action:'save', delayedSave:true, data:{flags + 4 fields/yr}}
//   valuation  → POST /user/report/save  FLAT JSON body, type:'valuation_main', toggles + per-year keys (no underscore)
//   company    → POST /user/report/save  empty body, single-last-year payload as QUERY PARAMS, type:'company'
import axios from 'axios';

const BASE = process.env.VUE_APP_API_BASE_URL;

function session() {
  return localStorage.getItem('vue-authenticate.vueauth_access_token');
}

// POST /user/my_data/get — the saved input set's per-year records.
// Year fields (camelCase): year, businessRevenue, grossMargin,
// costOfSalesLaborPortion, officersComp, salaryWages, rent, taxesPaid,
// advertising, benefitsPension, repairs, badDebt, salesGeneralAdminMisc,
// amortizationDepreciationDepletion, interestIncome, interestExpense,
// otherIncome, incomeTax, cash, receivables, inventory, otherCurrentAssets,
// grossFixedAssets, accumDepreciationAmortizationDepltn, netFixedAssets,
// otherNonCurrentAssets, accountsPayable, loansNotesPayable,
// otherCurrentLiabilities, totalLongTermLiabilities, employment, averageAnnualWage
const yearsCache = {};
export async function fetchMyDataYears(myDataId) {
  const key = String(myDataId);
  if (yearsCache[key]) return yearsCache[key];
  const res = await axios.post(BASE + '/user/my_data/get', {
    session_id: session(),
    my_data_id: myDataId,
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  const out = { years: res.data.years || [], myData: res.data.my_data || {} };
  yearsCache[key] = out;
  return out;
}

/* ---------------- financial (if / spif) ---------------- */
const IF_CHECKS = [
  'accounts_payable_business_revenue_check', 'ebitda_business_revenue_check', 'current_ratio_check',
  'return_on_assets_check', 'quick_ratio_check', 'return_on_net_worth_check', 'days_payable_check',
  'return_on_business_revenue_check', 'current_liabilities_inventory_check', 'discretionary_owner_earnings_check',
  'net_working_capital_check', 'return_on_assets_after_tax_check', 'cosPayables_check',
  'return_on_net_worth_after_tax_check', 'cosInventory_check', 'return_on_business_revenue_after_tax_check',
  'profit_employee_check', 'owner_earnings_employee_check', 'assets_business_revenue_check',
  'interest_coverage_check', 'days_inventory_check', 'current_liabilities_net_worth_check',
  'days_receivables_check', 'long_term_liabilities_net_worth_check', 'current_asset_turnover_check',
  'loans_notes_payable_net_worth_check', 'fixed_asset_turnover_check', 'zscore_check',
  'gross_margin_business_revenue_check', 'total_liabilities_net_worth_check', 'sales_per_employee_check',
  'fixedAssetsNetWorth_check', 'invTurnover_check', 'arTurnover_check', 'totalAssetTurnover_check',
  'workingCapitalTurnover_check', 'daysWorkingCapital_check', 'cashTurnover_check', 'efficiency_index_check',
  'year_0_check', 'year_1_check', 'year_2_check', 'year_3_check', 'year_4_check', 'year_5_check',
  'pl_balance_percent_check', 'pl_balance_dollar_check', 'source_uses_check', 'ratio_scores_check',
  'summary_scores_check',
];
const SPIF_CHECKS = [
  'year_0_check', 'year_1_check', 'year_2_check', 'year_3_check', 'net_cash_sales_ratio_check',
  'sales_net_cash_ratio_check', 'gross_profit_sales_ratio_check', 'EBITDA_sales_ratio_check',
  'net_profit_sales_ratio_check', 'cost_of_sales_inventory_ratio_check', 'days_inventory_check',
  'EBITDA_interest_check', 'sales_inventory_ratio_check', 'total_labor_revenue_ratio_check',
  'profit_per_employee_ratio_check', 'owner_earnings_per_employee_ratio_check', 'ratio_scores_check',
  'pl_balance_percent_check', 'pl_balance_dollar_check', 'summary_score_check',
];
// payload key prefix -> my_data year property (identical for if and spif)
const IF_YEAR_FIELDS = {
  year_field: 'year',
  other_netSales_field: 'businessRevenue',
  cosl_field: 'costOfSalesLaborPortion',
  other_grossProfit_field: 'grossMargin',
  other_offcomp_field: 'officersComp',
  other_payroll_field: 'salaryWages',
  other_rent_field: 'rent',
  other_taxes_field: 'taxesPaid',
  other_advertising_field: 'advertising',
  other_benefits_field: 'benefitsPension',
  other_repairs_field: 'repairs',
  other_baddebt_field: 'badDebt',
  other_other_field: 'salesGeneralAdminMisc',
  other_amortization_field: 'amortizationDepreciationDepletion',
  other_interestincome_field: 'interestIncome',
  other_interest_field: 'interestExpense',
  other_otherincome_field: 'otherIncome',
  asset_cash_field: 'cash',
  asset_accountsReceivable_field: 'receivables',
  asset_inventory_field: 'inventory',
  asset_otherCurrent_field: 'otherCurrentAssets',
  asset_gross_fixed_field: 'grossFixedAssets',
  asset_depreciation_amortization_depletion_field: 'accumDepreciationAmortizationDepltn',
  asset_fixed_field: 'netFixedAssets',
  asset_otherNonCurrent_field: 'otherNonCurrentAssets',
  liability_accountsPayable_field: 'accountsPayable',
  liability_loans_field: 'loansNotesPayable',
  liability_otherCurrent_field: 'otherCurrentLiabilities',
  liability_longTermDebt_field: 'totalLongTermLiabilities',
  ft_emps_field: 'employment',
  income_tax_field: 'incomeTax',
  avg_annual_wage_field: 'averageAnnualWage',
};

async function saveFinancial(profileId, years, soleProp) {
  const type = soleProp ? 'spif' : 'if';
  const data = {};
  (soleProp ? SPIF_CHECKS : IF_CHECKS).forEach((k) => { data[k] = true; });
  let i = 0;
  years.forEach((year) => {
    if (!year.year) return;
    Object.keys(IF_YEAR_FIELDS).forEach((k) => {
      data[k + '_' + i] = year[IF_YEAR_FIELDS[k]];
    });
    i++;
  });
  const res = await axios.post(BASE + '/user/report/save', {
    type,
    action: 'save',
    session_id: session(),
    profile_id: profileId,
    report_id: profileId,
    data,
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
}

/* ---------------- market (imr) ---------------- */
const IMR_CHECKS = IF_CHECKS.filter((k) => k !== 'year_5_check'); // classic imr sets year_0..4 + the ratio set

async function saveMarket(profileId, years) {
  const data = {};
  IMR_CHECKS.forEach((k) => { data[k] = true; });
  years.forEach((year, i) => {
    data['sales_' + i] = year.businessRevenue;
    data['rent_' + i] = year.rent;
    data['employment_' + i] = year.employment;
    data['avg_wage_' + i] = year.averageAnnualWage;
  });
  const res = await axios.post(BASE + '/user/report/save', {
    type: 'imr',
    action: 'save',
    session_id: session(),
    profile_id: profileId,
    report_id: profileId,
    delayedSave: true,
    data,
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
}

/* ---------------- valuation (flat payload, type valuation_main) ---------------- */
function num(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// derived exactly per CreateReportFormat.valuationData()
function valuationYear(y) {
  const opex = num(y.officersComp) + num(y.salaryWages) + num(y.rent) + num(y.taxesPaid) + num(y.advertising)
    + num(y.benefitsPension) + num(y.repairs) + num(y.badDebt) + num(y.salesGeneralAdminMisc)
    + num(y.amortizationDepreciationDepletion);
  return {
    year: y.year,
    businessRevenue: y.businessRevenue,
    grossMargin: y.grossMargin,
    officersComp: y.officersComp,
    preTaxNetProfit: num(y.grossMargin) - opex + num(y.interestIncome) - num(y.interestExpense) + num(y.otherIncome),
    incomeExpenseAddbacks: 0,
    depreciation: y.amortizationDepreciationDepletion,
    ownerReplacementCompensation: 0,
    operatingExpenses: opex,
    interestExpense: y.interestExpense,
    accountsReceivable: y.receivables,
    currentAssets: num(y.cash) + num(y.receivables) + num(y.inventory) + num(y.otherCurrentAssets),
    totalAssets: num(y.cash) + num(y.receivables) + num(y.inventory) + num(y.otherCurrentAssets)
      + num(y.netFixedAssets) + num(y.otherNonCurrentAssets),
    accountsPayable: y.accountsPayable,
    otherCurrentLiabilities: num(y.accountsPayable) + num(y.loansNotesPayable) + num(y.otherCurrentLiabilities),
    totalLongTermLiabilities: y.totalLongTermLiabilities,
    employment: y.employment,
    averageAnnualWage: y.averageAnnualWage,
  };
}

const VALUATION_TOGGLES = [
  'toggle_current_ratio_check', 'toggle_days_payable_check', 'toggle_owner_earnings_p_check',
  'toggle_owner_earnings_g_check', 'toggle_annual_sales_g_check', 'toggle_return_on_assets_check',
  'toggle_days_receivables_check', 'toggle_efficiency_index_check', 'toggle_modified_z_check',
  'toggle_interest_coverage_check', 'toggle_total_liabilities_nw_check', 'toggle_market_opp_index_check',
  'toggle_ind_market_growth_check',
];

async function saveValuation(profileId, years) {
  const data = {};
  VALUATION_TOGGLES.forEach((k) => { data[k] = true; });
  ['check_technology_level', 'check_customer_concentration', 'check_customer_base', 'check_key_employees']
    .forEach((k) => { data[k] = 'on'; });
  let i = 0;
  years.forEach((raw) => {
    if (raw.year == null) return;
    const y = valuationYear(raw);
    data['year_' + i] = y.year;
    data['year_' + i + '_check'] = true;
    // classic valuation keys have NO underscore before the index
    data['revenue' + i] = y.businessRevenue;
    data['gross_margin' + i] = y.grossMargin;
    data['officers_compensation' + i] = y.officersComp;
    data['pre_tax_net_profit' + i] = y.preTaxNetProfit;
    data['income_expense_addbacks' + i] = y.incomeExpenseAddbacks;
    data['depreciation' + i] = y.depreciation;
    data['owner_replacement_compensation' + i] = y.ownerReplacementCompensation;
    data['operating_expenses' + i] = y.operatingExpenses;
    data['interest_expense' + i] = y.interestExpense;
    data['accounts_receivable' + i] = y.accountsReceivable;
    data['current_assets' + i] = y.currentAssets;
    data['total_assets' + i] = y.totalAssets;
    data['accounts_payable' + i] = y.accountsPayable;
    data['current_liabilties' + i] = y.otherCurrentLiabilities; // classic's real (misspelled) key
    data['long_term_liabilities' + i] = y.totalLongTermLiabilities;
    data['ft_emps' + i] = y.employment;
    data['avg_annual_wage' + i] = y.averageAnnualWage;
    i++;
  });
  data.type = 'valuation_main';
  data.action = 'save';
  data.session_id = session();
  data.report_id = profileId;
  data.profile_id = profileId;
  data.delayedSave = true;
  const res = await axios.post(BASE + '/user/report/save', data);
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
}

/* ---------------- company / SWOT (single last year, query params) ---------------- */
async function saveCompany(profileId, years) {
  const y = years[years.length - 1] || {};
  const opex = num(y.officersComp) + num(y.salaryWages) + num(y.rent) + num(y.taxesPaid) + num(y.advertising)
    + num(y.benefitsPension) + num(y.repairs) + num(y.badDebt) + num(y.salesGeneralAdminMisc)
    + num(y.amortizationDepreciationDepletion);
  const params = {
    type: 'company',
    session_id: session(),
    profile_id: profileId,
    acc_receivable: y.receivables,
    other_netSales: y.businessRevenue,
    other_grossProfit: y.grossMargin,
    other_offcomp: y.officersComp,
    other_amortization: y.amortizationDepreciationDepletion,
    avg_annual_wage: y.averageAnnualWage,
    ft_emps: y.employment,
    liability_accountsPayable: y.accountsPayable,
    liability_otherCurrent: num(y.accountsPayable) + num(y.loansNotesPayable) + num(y.otherCurrentLiabilities),
    liability_longTermDebt: y.totalLongTermLiabilities,
    asset_otherCurrent: num(y.cash) + num(y.receivables) + num(y.inventory) + num(y.otherCurrentAssets),
    totalAssets: num(y.cash) + num(y.receivables) + num(y.inventory) + num(y.otherCurrentAssets)
      + num(y.netFixedAssets) + num(y.otherNonCurrentAssets),
    op_expenses: opex,
    net_profit: num(y.grossMargin) - opex + num(y.interestIncome) - num(y.interestExpense) + num(y.otherIncome),
  };
  const res = await axios.post(BASE + '/user/report/save', {}, { params });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
}

/* ---------------- dispatcher ---------------- */
// kind: 'financial' | 'market' | 'valuation' | 'company'
export async function attachReportInputs(kind, profileId, years, opts) {
  if (kind === 'financial') return saveFinancial(profileId, years, !!(opts && opts.soleProp));
  if (kind === 'market') return saveMarket(profileId, years);
  if (kind === 'valuation') return saveValuation(profileId, years);
  if (kind === 'company') return saveCompany(profileId, years);
  throw new Error('unknown report kind: ' + kind);
}

// post-attach destinations (classic parity, incl. the real "finanical" typo route)
export function reportAboutRoute(kind, profileId, soleProp) {
  if (kind === 'financial') {
    return { path: (soleProp ? '/sole-proprietorship-finanical-report/' : '/finanical-report/') + profileId + '/about-data' };
  }
  if (kind === 'market') return { path: '/market-report/' + profileId + '/market-rep-about-data' };
  if (kind === 'valuation') return { name: 'VRAbouttheData', params: { id: profileId } };
  return { path: '/company-report/' + profileId + '/report-about-the-data' };
}
